import {
  FormContext,
  FormInputGroupLayoutContext,
  FormState,
  optimizedRetryOption,
  Select,
  SelectProps,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import { NormalError } from "savant-connector";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { ttlOneMinCaching } from "../../constants/uiConstants";
import {
  CloudExadataInfrastructureSummary,
  CloudExadataInfrastructureSummaryCollection,
} from "../../gen/clients/mchub-azure-api-client-exa";
import { IdResourceType } from "../../helpers/idHelper";
import { responseItemstoArray } from "../../helpers/resourceHelper";
import { useQueryCall } from "../../hooks/useQueryCall";
import { getOciRegion } from "../../utils";

export interface CloudInfraProps extends Omit<SelectProps, "options"> {
  resourceGroupName: string;
  subscriptionId: string;
  location: string;
  onError?: (error: NormalError) => void;
  onMissingDependencies?: (missingDependencies: IdResourceType[]) => void;
  onInfraChange?: (infra: CloudExadataInfrastructureSummary) => void;
}

export const CloudInfrastructureSelect = (
  {
    defaultValue,
    fieldName,
    label,
    placeholder,
    required,
    subField,
    tooltip,
    validator,
    subscriptionId,
    resourceGroupName,
    location,
    onError,
    onMissingDependencies,
    disabled,
    onInfraChange,
    testId,
  }: CloudInfraProps,
): JSX.Element => {
  const [key, setKey] = React.useState<string>(uniqueGUID());

  const { groupName } = React.useContext(
    FormInputGroupLayoutContext,
  );

  const form: FormState = React.useContext(FormContext);

  const { loading, response, error } = useQueryCall({
    wait: !(subscriptionId && resourceGroupName && location),
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.listCloudExadataInfrastructures,
    options: {
      args: { subscriptionId, resourceGroupName, apiVersion: MultiCloudDatabaseApiVersion },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadExadbInfraList(),
      },
    },
  });
  // eslint-disable-next-line max-len
  const items = (response && responseItemstoArray<CloudExadataInfrastructureSummaryCollection, CloudExadataInfrastructureSummary>(response)) || [];
  // eslint-disable-next-line max-len
  const options = items?.filter(item => item.lifecycleState !== "TERMINATED" && item.lifecycleState !== "TERMINATING" && item.status !== "Failed")
    .map(item => ({
      id: item.id,
      text: item.name || "",
    }));

  React.useEffect(() => {
    form.setValue(undefined, fieldName, groupName);
    setKey(uniqueGUID());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceGroupName, subscriptionId, location]);

  React.useEffect(() => {
    setKey(uniqueGUID());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(items)]);

  React.useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const internalOnClick = (): void => {
    const missingDependencies: IdResourceType[] = [];
    if (!subscriptionId) missingDependencies.push(IdResourceType.SUBSCRIPTIONS);
    if (!resourceGroupName) missingDependencies.push(IdResourceType.RESOURCE_GROUPS);
    if (!location) missingDependencies.push(IdResourceType.LOCATION);
    if (missingDependencies.length > 0) {
      onMissingDependencies?.(missingDependencies);
    }
  };

  const onInternalChange = (infraId: string): void => {
    const selectedInfra = items.find(item => item.id === infraId);
    if (selectedInfra !== undefined) {
      onInfraChange?.(selectedInfra);
    }
  };

  const derivedPlaceholder = !loading && !error && options.length === 0
    ? Messages.createCommon.loadingNone(label?.toLowerCase() || "")
    : placeholder;

  return (
    <Select
      key={key}
      testId={testId}
      loading={!!resourceGroupName && !!subscriptionId && !!location && loading}
      required={required}
      subField={subField}
      fieldName={fieldName}
      label={label}
      options={options}
      placeholder={derivedPlaceholder}
      defaultValue={defaultValue}
      tooltip={tooltip}
      onClick={internalOnClick}
      onChange={onInternalChange}
      disabled={disabled}
      validator={validator}
    />
  );
};
